/**
 * useProgressTracking Hook
 * Manages multi-step progress state and time estimation for load/save operations.
 *
 * Time estimation is based on historical step durations stored in localStorage.
 * Each operation/step combination maintains a rolling average of the last N samples.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { ProgressStep, ProgressState } from '../services/types';

const STORAGE_KEY = 'brr_progress_timings';
const MAX_SAMPLES_PER_STEP = 5;
const DEFAULT_STEP_DURATION_MS = 200;

interface StepTimingHistory {
  [operationId: string]: {
    [stepName: string]: number[];
  };
}

interface UseProgressTrackingOptions {
  enabled?: boolean;
}

interface UseProgressTrackingReturn {
  progressState: ProgressState | null;
  startOperation: (operationId: string, steps: ProgressStep[], title?: string) => void;
  advanceStep: (stepLabel?: string) => void;
  completeOperation: () => void;
  cancelOperation: () => void;
}

function loadTimingHistory(): StepTimingHistory {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

function saveTimingHistory(history: StepTimingHistory): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(history));
  } catch {
    // localStorage may be full or unavailable
  }
}

function recordStepDuration(
  history: StepTimingHistory,
  operationId: string,
  stepName: string,
  durationMs: number
): StepTimingHistory {
  const updated = { ...history };
  if (!updated[operationId]) {
    updated[operationId] = {};
  }
  if (!updated[operationId][stepName]) {
    updated[operationId][stepName] = [];
  }

  const samples = [...updated[operationId][stepName], durationMs];
  // Keep only the last N samples
  if (samples.length > MAX_SAMPLES_PER_STEP) {
    samples.shift();
  }
  updated[operationId][stepName] = samples;

  return updated;
}

function getAverageStepDuration(
  history: StepTimingHistory,
  operationId: string,
  stepName: string
): number | null {
  const samples = history[operationId]?.[stepName];
  if (!samples || samples.length === 0) {
    return null;
  }
  return samples.reduce((sum, d) => sum + d, 0) / samples.length;
}

function calculateEstimatedTimeRemaining(
  history: StepTimingHistory,
  operationId: string,
  steps: ProgressStep[],
  currentStepIndex: number
): number | null {
  let totalEstimate = 0;
  let hasAnyEstimate = false;

  // Estimate remaining steps (including current step's remaining time)
  for (let i = currentStepIndex; i < steps.length; i++) {
    const step = steps[i];
    const weight = step.weight ?? 1;
    const avgDuration = getAverageStepDuration(history, operationId, step.name);

    if (avgDuration !== null) {
      totalEstimate += avgDuration;
      hasAnyEstimate = true;
    } else {
      // Use default duration scaled by weight for unknown steps
      totalEstimate += DEFAULT_STEP_DURATION_MS * weight;
    }
  }

  return hasAnyEstimate ? totalEstimate : null;
}

function calculatePercentage(
  currentStep: number,
  steps: ProgressStep[]
): number {
  if (steps.length === 0) return 0;

  // Calculate total weight
  const totalWeight = steps.reduce((sum, step) => sum + (step.weight ?? 1), 0);

  // Calculate completed weight
  let completedWeight = 0;
  for (let i = 0; i < currentStep; i++) {
    completedWeight += steps[i].weight ?? 1;
  }

  return Math.round((completedWeight / totalWeight) * 100);
}

export function useProgressTracking(
  options: UseProgressTrackingOptions = {}
): UseProgressTrackingReturn {
  const { enabled = true } = options;

  const [progressState, setProgressState] = useState<ProgressState | null>(null);

  // Refs to track timing without causing re-renders
  const stepsRef = useRef<ProgressStep[]>([]);
  const operationIdRef = useRef<string>('');
  const stepStartTimeRef = useRef<number>(0);
  const timingHistoryRef = useRef<StepTimingHistory>(loadTimingHistory());

  // Load timing history on mount
  useEffect(() => {
    timingHistoryRef.current = loadTimingHistory();
  }, []);

  const startOperation = useCallback((
    operationId: string,
    steps: ProgressStep[],
    title?: string
  ) => {
    if (!enabled || steps.length === 0) return;

    stepsRef.current = steps;
    operationIdRef.current = operationId;
    stepStartTimeRef.current = Date.now();

    const estimatedTime = calculateEstimatedTimeRemaining(
      timingHistoryRef.current,
      operationId,
      steps,
      0
    );

    setProgressState({
      isActive: true,
      operationId,
      currentStep: 0,
      totalSteps: steps.length,
      currentStepLabel: steps[0].label,
      percentage: 0,
      estimatedTimeRemaining: estimatedTime,
      title,
    });
  }, [enabled]);

  const advanceStep = useCallback((stepLabel?: string) => {
    if (!enabled) return;

    setProgressState(prev => {
      if (!prev || !prev.isActive) return prev;

      const steps = stepsRef.current;
      const operationId = operationIdRef.current;

      // Record duration of the step we're leaving
      const stepDuration = Date.now() - stepStartTimeRef.current;
      const completedStepName = steps[prev.currentStep]?.name;

      if (completedStepName) {
        timingHistoryRef.current = recordStepDuration(
          timingHistoryRef.current,
          operationId,
          completedStepName,
          stepDuration
        );
        // Persist to localStorage (debounce in production if needed)
        saveTimingHistory(timingHistoryRef.current);
      }

      // Move to next step
      const nextStep = prev.currentStep + 1;
      stepStartTimeRef.current = Date.now();

      if (nextStep >= steps.length) {
        // Operation complete
        return {
          ...prev,
          currentStep: steps.length,
          percentage: 100,
          currentStepLabel: stepLabel || 'Complete',
          estimatedTimeRemaining: 0,
        };
      }

      const estimatedTime = calculateEstimatedTimeRemaining(
        timingHistoryRef.current,
        operationId,
        steps,
        nextStep
      );

      return {
        ...prev,
        currentStep: nextStep,
        currentStepLabel: stepLabel || steps[nextStep].label,
        percentage: calculatePercentage(nextStep, steps),
        estimatedTimeRemaining: estimatedTime,
      };
    });
  }, [enabled]);

  const completeOperation = useCallback(() => {
    if (!enabled) return;

    // Record final step duration if there was one
    setProgressState(prev => {
      if (!prev || !prev.isActive) return prev;

      const steps = stepsRef.current;
      const operationId = operationIdRef.current;
      const completedStepName = steps[prev.currentStep]?.name;

      if (completedStepName) {
        const stepDuration = Date.now() - stepStartTimeRef.current;
        timingHistoryRef.current = recordStepDuration(
          timingHistoryRef.current,
          operationId,
          completedStepName,
          stepDuration
        );
        saveTimingHistory(timingHistoryRef.current);
      }

      return null;
    });
  }, [enabled]);

  const cancelOperation = useCallback(() => {
    setProgressState(null);
    stepsRef.current = [];
    operationIdRef.current = '';
  }, []);

  return {
    progressState,
    startOperation,
    advanceStep,
    completeOperation,
    cancelOperation,
  };
}

export default useProgressTracking;
